/**
 * Intern dashboard voor Core Web Vitals bij echte bezoekers.
 *
 * Achter dezelfde adminbeveiliging als het leadoverzicht. Bij het verwachte
 * volume (10-50 bezoekers per dag) staat de trend standaard op maandbasis;
 * dag- en weekniveau blijven beschikbaar als inzoom, met een expliciete
 * betrouwbaarheidsmelding zodra het aantal metingen te klein wordt.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getVitalsDashboard,
  getVitalsLive,
  getVitalsYearOverYear,
  rollupVitalsMonthly,
} from "@/lib/vitals-admin.functions";
import {
  CORE_METRICS,
  METRIC_INFO,
  METRIC_NAMES,
  THRESHOLDS,
  formatMetric,
  type MetricName,
  type Rating,
} from "@/lib/web-vitals";
import {
  GRADE_CLASS,
  GRADE_LABEL,
  GRADE_SCALE_TEXT,
  excellentThreshold,
  gradeMetric,
} from "@/lib/score-grades";
import { VitalsRouteDetail } from "@/components/admin/vitals-route-detail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/vitals")({
  head: () => ({
    meta: [
      { title: "Web Vitals | Beheer IT Solutions" },
      {
        name: "description",
        content: "Core Web Vitals bij echte bezoekers, per route en apparaat.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Web Vitals | Beheer IT Solutions" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: VitalsPage,
});

type Device = "all" | "mobile" | "desktop";
type Granularity = "day" | "week" | "month";

const RATING_CLASS: Record<Rating, string> = {
  good: "bg-emerald-surface text-emerald-ink border-emerald-border",
  "needs-improvement": "bg-amber-surface text-amber-ink border-amber-border",
  poor: "bg-destructive/10 text-destructive border-destructive/30",
};

const RATING_LABEL: Record<Rating, string> = {
  good: "goed",
  "needs-improvement": "matig",
  poor: "slecht",
};

/** Drempels als leesbare zin: "goed ≤ 2,50 s · slecht > 4,00 s". */
function thresholdText(metric: MetricName): string {
  const t = THRESHOLDS[metric];
  return `goed ≤ ${formatMetric(metric, t.good)} · slecht > ${formatMetric(metric, t.poor)}`;
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-6">
      <header className="mb-4">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

function LowSampleNote({ samples, minimum }: { samples: number; minimum: number }) {
  if (samples >= minimum) return null;
  return (
    <p className="mt-3 rounded-md border border-amber-border bg-amber-surface px-3 py-2 text-xs text-amber-ink">
      {/* i18n-ignore */}
      Te weinig metingen ({samples} van minstens {minimum}) voor een betrouwbare p75. Lees deze
      cijfers als indicatie, niet als trend.
    </p>
  );
}

function VitalsPage() {
  const [device, setDevice] = useState<Device>("all");
  const [days, setDays] = useState(28);
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [yoyMetric, setYoyMetric] = useState<MetricName>("LCP");
  const [yoyYears, setYoyYears] = useState(2);
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);

  const [liveMinutes, setLiveMinutes] = useState(60);

  const dashboardFn = useServerFn(getVitalsDashboard);
  const liveFn = useServerFn(getVitalsLive);
  const yoyFn = useServerFn(getVitalsYearOverYear);
  const rollupFn = useServerFn(rollupVitalsMonthly);
  const queryClient = useQueryClient();

  const dash = useQuery({
    queryKey: ["vitals", days, device, granularity],
    queryFn: () => dashboardFn({ data: { days, device, granularity } }),
  });

  // Live: ververst automatisch, zodat metingen binnenlopen terwijl u kijkt.
  const live = useQuery({
    queryKey: ["vitals-live", liveMinutes],
    queryFn: () => liveFn({ data: { minutes: liveMinutes, limit: 60 } }),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  const yoy = useQuery({
    queryKey: ["vitals-yoy", yoyMetric, device, yoyYears],
    queryFn: () => yoyFn({ data: { metric: yoyMetric, device, years: yoyYears } }),
  });

  const rollup = useMutation({
    mutationFn: () => rollupFn({ data: { months: 2 } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vitals-yoy"] });
    },
  });

  const data = dash.data;
  const minimum = data?.minReliableSamples ?? 50;

  const distributionData = useMemo(
    () =>
      (data?.distribution ?? []).map((row) => ({
        metric: row.metric,
        goed: row.good,
        matig: row.needsImprovement,
        slecht: row.poor,
      })),
    [data],
  );

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-8">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/admin/leads">{/* i18n-ignore */}← Terug naar leads</Link>
      </Button>

      <div className="mb-6 mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Web Vitals</h1>
          <p className="text-sm text-muted-foreground">
            Gemeten bij echte bezoekers, zonder cookies. {data?.totalSamples ?? 0} metingen in de
            gekozen periode.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(["all", "mobile", "desktop"] as Device[]).map((d) => (
            <Button
              key={d}
              size="sm"
              variant={device === d ? "default" : "outline"}
              onClick={() => setDevice(d)}
            >
              {d === "all" ? "Alle apparaten" : d === "mobile" ? "Mobiel" : "Desktop"}
            </Button>
          ))}
          {[28, 90, 365].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
            >
              {d} dagen
            </Button>
          ))}
        </div>
      </div>

      {dash.isLoading ? <p className="text-sm text-muted-foreground">Bezig met laden…</p> : null}
      {dash.error ? (
        <p className="text-sm text-destructive">Kon de metingen niet laden: {String(dash.error)}</p>
      ) : null}

      <div className="grid gap-6">
        {/* 0. Live — echte bezoekers, ververst elke 15 seconden */}
        <Panel
          /* i18n-ignore: interne adminpagina, alleen NL */
          title="Live — echte bezoekers"
          subtitle="Ruwe metingen zoals ze binnenkomen (geen labscore). Ververst automatisch elke 15 seconden. Een meting vertrekt pas wanneer de bezoeker de pagina verlaat of naar de achtergrond zet."
        >
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {[15, 60, 360, 1440].map((m) => (
              <Button
                key={m}
                size="sm"
                variant={liveMinutes === m ? "default" : "outline"}
                onClick={() => setLiveMinutes(m)}
              >
                {m < 60 ? `${m} min` : m < 1440 ? `${m / 60} uur` : "24 uur"}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground">
              {live.data ? `${live.data.totalSamples} metingen in dit venster` : "…"}
              {live.isFetching ? " · verversen…" : ""}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(live.data?.latest ?? []).map((s) => (
              <div key={s.metric} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">{s.metric}</span>
                  {s.rating ? (
                    <Badge variant="outline" className={RATING_CLASS[s.rating]}>
                      {RATING_LABEL[s.rating]}
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 font-display text-2xl">{formatMetric(s.metric, s.value)}</p>
                <p className="text-xs text-muted-foreground">
                  laatste meting · p75 {formatMetric(s.metric, s.p75)} · {s.samples}×
                </p>
              </div>
            ))}
          </div>

          {live.data && live.data.recent.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-3 font-medium">Tijd</th>
                    <th className="py-1 pr-3 font-medium">Metriek</th>
                    <th className="py-1 pr-3 font-medium">Waarde</th>
                    <th className="py-1 pr-3 font-medium">Pagina</th>
                    <th className="py-1 pr-3 font-medium">Apparaat</th>
                    <th className="py-1 font-medium">Verbinding</th>
                  </tr>
                </thead>
                <tbody>
                  {live.data.recent.slice(0, 25).map((r, i) => (
                    <tr key={`${r.createdAt}-${r.metric}-${i}`} className="border-t border-border">
                      <td className="py-1 pr-3 tabular-nums">
                        {new Date(r.createdAt).toLocaleTimeString("nl-BE")}
                      </td>
                      <td className="py-1 pr-3">{r.metric}</td>
                      <td className="py-1 pr-3 tabular-nums">
                        <span className={`rounded px-1.5 py-0.5 ${RATING_CLASS[r.rating]}`}>
                          {formatMetric(r.metric, r.value)}
                        </span>
                      </td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.route}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.device}</td>
                      <td className="py-1 text-muted-foreground">{r.connection ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">
              Nog geen metingen in dit venster. Metingen komen alleen van de gepubliceerde site — in
              de preview wordt bewust niets weggeschreven.
            </p>
          )}
        </Panel>

        {/* 1. Statusbalk */}
        <Panel
          title="Status (p75)"
          subtitle={`Laatste ${days} dagen · ${device === "all" ? "alle apparaten samen" : device === "mobile" ? "mobiel" : "desktop"}`}
        >
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(data?.summaries ?? []).map((s) => {
              const grade = gradeMetric(s.metric, s.p75);
              return (
                <div key={s.metric} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span
                      className="text-xs font-medium text-muted-foreground"
                      title={METRIC_INFO[s.metric].fullName}
                    >
                      {s.metric}
                    </span>
                    {grade ? (
                      <Badge variant="outline" className={GRADE_CLASS[grade]}>
                        {GRADE_LABEL[grade]}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {METRIC_INFO[s.metric].fullName}
                  </p>
                  <p className="mt-1 font-display text-2xl">{formatMetric(s.metric, s.p75)}</p>
                  <p className="text-xs text-muted-foreground">
                    mediaan {formatMetric(s.metric, s.p50)} · {s.samples} metingen
                    {s.reliable ? "" : " (te weinig)"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{thresholdText(s.metric)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    uitstekend ≤ {formatMetric(s.metric, excellentThreshold(s.metric))}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{GRADE_SCALE_TEXT}</p>
          {device === "all" ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: bekijk mobiel en desktop apart — samengevoegd verbergen ze elkaars problemen.
            </p>
          ) : null}
        </Panel>

        {/* 1b. Uitleg metrieken en drempels */}
        <Panel
          /* i18n-ignore: interne adminpagina, alleen NL */
          title="Wat meten we?"
          subtitle="Officiële Google-metrieken (zoals in CrUX en Search Console) met hun drempels. p75 = 75% van de bezoekers had het minstens zo goed."
        >
          <ul className="grid gap-3 text-sm md:grid-cols-2 lg:grid-cols-3">
            {METRIC_NAMES.map((m) => (
              <li key={m} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {m}{" "}
                    <span className="font-normal text-muted-foreground">
                      — {METRIC_INFO[m].fullName}
                    </span>
                  </span>
                  {METRIC_INFO[m].core ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-surface text-emerald-ink border-emerald-border"
                    >
                      Core
                    </Badge>
                  ) : (
                    <Badge variant="outline">diagnostisch</Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{METRIC_INFO[m].description}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{thresholdText(m)}</p>
              </li>
            ))}
          </ul>
        </Panel>

        {/* 2. Verdeling */}
        <Panel title="Verdeling per metriek" subtitle="Aandeel goed / matig / slecht in procent">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distributionData} layout="vertical" stackOffset="expand">
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" tickFormatter={(v: number) => `${Math.round(v * 100)}%`} />
                <YAxis type="category" dataKey="metric" width={56} />
                <Tooltip formatter={(v: number) => `${v}%`} />
                <Legend />
                <Bar dataKey="goed" stackId="a" fill="var(--chart-1)" />
                <Bar dataKey="matig" stackId="a" fill="var(--chart-4)" />
                <Bar dataKey="slecht" stackId="a" fill="var(--chart-5)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* 3. Trend */}
        <Panel
          title="Trend p75"
          subtitle="Standaard per maand: bij dit verkeersvolume is dat het fijnste niveau met betekenisvolle cijfers."
        >
          <div className="mb-3 flex gap-2">
            {(["month", "week", "day"] as Granularity[]).map((g) => (
              <Button
                key={g}
                size="sm"
                variant={granularity === g ? "default" : "outline"}
                onClick={() => setGranularity(g)}
              >
                {g === "month" ? "Per maand" : g === "week" ? "Per week" : "Per dag"}
              </Button>
            ))}
          </div>
          {granularity !== "month" ? (
            <p className="mb-3 rounded-md border border-amber-border bg-amber-surface px-3 py-2 text-xs text-amber-ink">
              Inzoom op {granularity === "week" ? "week" : "dag"}niveau: de meeste punten steunen op
              enkele metingen en zijn dus ruis, geen trend.
            </p>
          ) : null}
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trend ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="bucket" />
                <YAxis yAxisId="ms" />
                <YAxis yAxisId="cls" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="ms"
                  type="monotone"
                  dataKey="LCP"
                  stroke="var(--chart-1)"
                  dot={false}
                />
                <Line
                  yAxisId="ms"
                  type="monotone"
                  dataKey="INP"
                  stroke="var(--chart-2)"
                  dot={false}
                />
                <Line
                  yAxisId="ms"
                  type="monotone"
                  dataKey="FCP"
                  stroke="var(--chart-3)"
                  dot={false}
                />
                <Line
                  yAxisId="ms"
                  type="monotone"
                  dataKey="TTFB"
                  stroke="var(--chart-4)"
                  dot={false}
                />
                <Line
                  yAxisId="cls"
                  type="monotone"
                  dataKey="CLS"
                  stroke="var(--chart-5)"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* 4. Pagina-tabel */}
        <Panel
          title="Per pagina"
          subtitle="p75 per route, gesorteerd op aantal metingen. Klik op een route voor de historiek en het verbeterplan."
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Route</th>
                  {METRIC_NAMES.map((m) => (
                    <th key={m} className="py-2" title={METRIC_INFO[m].fullName}>
                      {m}
                    </th>
                  ))}
                  <th className="py-2">Metingen</th>
                </tr>
              </thead>
              <tbody>
                {(data?.routes ?? []).map((row) => (
                  <tr
                    key={row.route}
                    onClick={() => setSelectedRoute(row.route)}
                    className={`cursor-pointer border-t border-border transition-colors hover:bg-muted/50 ${
                      selectedRoute === row.route ? "bg-muted/60" : ""
                    }`}
                  >
                    <td className="py-2 font-mono text-xs">
                      <button
                        type="button"
                        className="underline-offset-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRoute(row.route);
                        }}
                      >
                        {row.route}
                      </button>
                    </td>
                    {METRIC_NAMES.map((m) => {
                      const grade = gradeMetric(m, row[m] ?? null);
                      return (
                        <td key={m} className="py-2">
                          <span
                            className={`rounded border px-1.5 py-0.5 text-xs ${grade ? GRADE_CLASS[grade] : "border-transparent"}`}
                            title={grade ? GRADE_LABEL[grade] : undefined}
                          >
                            {formatMetric(m, row[m] ?? null)}
                          </span>
                        </td>
                      );
                    })}
                    <td className="py-2 text-muted-foreground">{row.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LowSampleNote samples={data?.totalSamples ?? 0} minimum={minimum} />
        </Panel>

        {/* 4b. Detail van de geselecteerde pagina */}
        {selectedRoute ? (
          <VitalsRouteDetail
            route={selectedRoute}
            device={device}
            onClose={() => setSelectedRoute(null)}
          />
        ) : null}

        {/* 5. Oorzaken */}
        <Panel
          title="Vermoedelijke oorzaken"
          subtitle="Uit de attributiedata van het web-vitals-pakket"
        >
          <div className="grid gap-4 md:grid-cols-3">
            {CORE_METRICS.map((metric) => (
              <div key={metric}>
                <h3 className="mb-2 text-sm font-medium">{metric}</h3>
                <ul className="space-y-1 text-xs">
                  {(data?.causes?.[metric] ?? []).map((cause) => (
                    <li key={cause.label} className="rounded-md border border-border px-2 py-1">
                      <span className="block truncate font-mono">{cause.label}</span>
                      <span className="text-muted-foreground">
                        {cause.count}× · p75 {formatMetric(metric, cause.p75)}
                      </span>
                    </li>
                  ))}
                  {!(data?.causes?.[metric] ?? []).length ? (
                    <li className="text-muted-foreground">Nog geen attributiedata.</li>
                  ) : null}
                </ul>
              </div>
            ))}
          </div>
        </Panel>

        {/* 6. Segmenten */}
        <Panel title="Segmenten" subtitle="Apparaat en verbindingstype">
          <div className="grid gap-6 md:grid-cols-2">
            {[
              { title: "Apparaat", rows: data?.devices ?? [] },
              { title: "Verbinding", rows: data?.connections ?? [] },
            ].map((block) => (
              <div key={block.title}>
                <h3 className="mb-2 text-sm font-medium">{block.title}</h3>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-1">Segment</th>
                      {CORE_METRICS.map((m) => (
                        <th key={m} className="py-1">
                          {m}
                        </th>
                      ))}
                      <th className="py-1">n</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row) => (
                      <tr key={row.segment} className="border-t border-border">
                        <td className="py-1">{row.segment}</td>
                        {CORE_METRICS.map((m) => (
                          <td key={m} className="py-1">
                            {formatMetric(m, row[m] ?? null)}
                          </td>
                        ))}
                        <td className="py-1 text-muted-foreground">{row.samples}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </Panel>

        {/* 7. Betrouwbaarheid */}
        <Panel
          title="Betrouwbaarheid"
          subtitle="Bij laag verkeer telt het aantal metingen zwaarder dan het cijfer"
        >
          <ul className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {(data?.summaries ?? []).map((s) => (
              <li
                key={s.metric}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2"
              >
                <span>{s.metric}</span>
                <span className={s.reliable ? "text-emerald-ink" : "text-amber-ink"}>
                  {s.samples} metingen{" "}
                  {s.reliable ? "· betrouwbaar" : `· minstens ${minimum} nodig`}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        {/* 8. Jaar-op-jaar */}
        <Panel
          title="Jaar-op-jaar"
          subtitle="p75 per maand uit de permanente maandsamenvatting; dezelfde maand naast vorige jaren."
        >
          <div className="mb-3 flex flex-wrap gap-2">
            {METRIC_NAMES.map((m) => (
              <Button
                key={m}
                size="sm"
                variant={yoyMetric === m ? "default" : "outline"}
                onClick={() => setYoyMetric(m)}
                title={METRIC_INFO[m].fullName}
              >
                {m}
              </Button>
            ))}
            {[2, 3, 5].map((y) => (
              <Button
                key={y}
                size="sm"
                variant={yoyYears === y ? "default" : "outline"}
                onClick={() => setYoyYears(y)}
              >
                {y} jaar
              </Button>
            ))}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => rollup.mutate()}
              disabled={rollup.isPending}
            >
              {rollup.isPending ? "Bezig…" : "Samenvatting herberekenen"}
            </Button>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yoy.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                {(yoy.data?.years ?? []).map((year, index) => (
                  <Line
                    key={year}
                    type="monotone"
                    dataKey={String(year)}
                    stroke={`var(--chart-${(index % 5) + 1})`}
                    connectNulls
                    dot
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {!(yoy.data?.series ?? []).some((p) =>
            (yoy.data?.years ?? []).some((y) => p[String(y)] !== null),
          ) ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Nog geen maandsamenvatting. Klik op “Samenvatting herberekenen” zodra er metingen
              zijn.
            </p>
          ) : null}
        </Panel>
      </div>
    </main>
  );
}
